import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { safeFileStem } from './shipmentPackingList';

const HEADERS = [
  'NO',
  'SUB NO',
  'LENGTH',
  'WIDTH',
  'HEIGHT',
  'KG',
  'CBM',
  'CARGO TYPE',
  'DESCRIPTION',
  'D2D',
  'C TYPE',
  'PCS',
  'T WEIGHT',
  'T CBM',
];

function buildAoaWithMerges(rows) {
  const aoa = [HEADERS];
  const merges = [];

  for (let i = 0; i < rows.length; i += 1) {
    const line = rows[i];
    aoa.push([
      line.isConsolidatedLayout
        ? (line.isFirstInMaster ? line.collectionNo : '')
        : (line.isFirstInGroup ? line.collectionNo : ''),
      line.subNo,
      line.length,
      line.width,
      line.height,
      line.kg,
      line.cbm,
      line.cargoType,
      line.description,
      line.isConsolidatedLayout
        ? (line.isFirstInD2dGroup ? line.d2d : '')
        : line.d2d,
      line.isConsolidatedLayout
        ? (line.isFirstInD2dGroup ? line.clearingType : '')
        : (line.isFirstInGroup ? line.clearingType : ''),
      line.isConsolidatedLayout
        ? (line.isFirstInMaster ? line.pcs : '')
        : (line.isFirstInGroup ? line.pcs : ''),
      line.isConsolidatedLayout
        ? (line.isFirstInMaster ? line.fWeight : '')
        : (line.isFirstInGroup ? line.fWeight : ''),
      line.isConsolidatedLayout
        ? (line.isFirstInMaster ? line.fCbm : '')
        : (line.isFirstInGroup ? line.fCbm : ''),
    ]);

    const excelRow = aoa.length - 1;

    if (line.isConsolidatedLayout) {
      if (line.isFirstInMaster && line.masterGroupSize > 1) {
        for (const c of [0, 11, 12, 13]) {
          merges.push({
            s: { r: excelRow, c },
            e: { r: excelRow + line.masterGroupSize - 1, c },
          });
        }
      }
      if (line.isFirstInD2dGroup && line.d2dGroupSize > 1) {
        for (const c of [9, 10]) {
          merges.push({
            s: { r: excelRow, c },
            e: { r: excelRow + line.d2dGroupSize - 1, c },
          });
        }
      }
    } else if (line.isFirstInGroup && (line.groupSize || 1) > 1) {
      const n = line.groupSize || 1;
      for (const c of [0, 10, 11, 12, 13]) {
        merges.push({
          s: { r: excelRow, c },
          e: { r: excelRow + n - 1, c },
        });
      }
    }
  }

  return { aoa, merges };
}

/**
 * @param {{ rows: object[], shipmentCode?: string, trackingNumber?: string }} opts
 */
export function downloadPackingListExcel(opts) {
  const { rows, shipmentCode, trackingNumber } = opts;
  const { aoa, merges } = buildAoaWithMerges(rows);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  if (merges.length) ws['!merges'] = merges;

  /* Match on-screen packing list: use width for readable print/export. */
  const colWidths = [
    { wch: 11 },
    { wch: 7 },
    { wch: 8 },
    { wch: 8 },
    { wch: 8 },
    { wch: 9 },
    { wch: 9 },
    { wch: 22 },
    { wch: 36 },
    { wch: 8 },
    { wch: 12 },
    { wch: 8 },
    { wch: 10 },
    { wch: 10 },
  ];
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Packing list');
  const stem = safeFileStem(shipmentCode, trackingNumber);
  XLSX.writeFile(wb, `${stem}_packing-list.xlsx`);
}

/** Same merged-cell logic as the on-screen / print packing list (jspdf-autotable rowSpan). */
function buildPackingListPdfBody(rows) {
  const mergedCellStyle = {
    valign: 'top',
    halign: 'center',
    fontStyle: 'bold',
  };
  const body = [];

  for (const r of rows) {
    const mid = [
      String(r.subNo ?? ''),
      String(r.length || '—'),
      String(r.width || '—'),
      String(r.height || '—'),
      String(r.kg || '—'),
      String(r.cbm || '—'),
      String(r.cargoType || '—'),
      String(r.description || '—'),
    ];

    if (r.isConsolidatedLayout) {
      const row = [];
      if (r.isFirstInMaster) {
        row.push({
          content: String(r.collectionNo || '—'),
          rowSpan: r.masterGroupSize || 1,
          styles: { ...mergedCellStyle },
        });
      }
      row.push(...mid);
      if (r.isFirstInD2dGroup) {
        row.push(
          {
            content: String(r.d2d || '—'),
            rowSpan: r.d2dGroupSize || 1,
            styles: { ...mergedCellStyle },
          },
          {
            content: String(r.clearingType || '—'),
            rowSpan: r.d2dGroupSize || 1,
            styles: { ...mergedCellStyle },
          },
        );
      }
      if (r.isFirstInMaster) {
        row.push(
          { content: String(r.pcs ?? ''), rowSpan: r.masterGroupSize || 1, styles: { ...mergedCellStyle } },
          { content: String(r.fWeight ?? ''), rowSpan: r.masterGroupSize || 1, styles: { ...mergedCellStyle } },
          { content: String(r.fCbm ?? ''), rowSpan: r.masterGroupSize || 1, styles: { ...mergedCellStyle } },
        );
      }
      body.push(row);
      continue;
    }

    const n = r.groupSize || 1;
    const regularMid = [...mid, String(r.d2d || '—')];
    if (r.isFirstInGroup) {
      body.push([
        { content: String(r.collectionNo || '—'), rowSpan: n, styles: { ...mergedCellStyle } },
        ...regularMid,
        { content: String(r.clearingType || '—'), rowSpan: n, styles: { ...mergedCellStyle } },
        { content: String(r.pcs ?? ''), rowSpan: n, styles: { ...mergedCellStyle } },
        { content: String(r.fWeight ?? ''), rowSpan: n, styles: { ...mergedCellStyle } },
        { content: String(r.fCbm ?? ''), rowSpan: n, styles: { ...mergedCellStyle } },
      ]);
    } else {
      body.push(regularMid);
    }
  }

  return body;
}

/**
 * A4 portrait PDF — full-width grid, light header, merged NO / C TYPE / PCS / T WEIGHT / T CBM like the app table.
 * Shared by download and print so both paths render identically.
 * @param {{ rows: object[], shipmentCode?: string, trackingNumber?: string }} opts
 * @returns {import('jspdf').jsPDF}
 */
function buildPackingListPdfDoc(opts) {
  const { rows, shipmentCode, trackingNumber } = opts;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const title = `Packing list — ${shipmentCode || trackingNumber || 'Shipment'}`;

  const body = buildPackingListPdfBody(rows);

  const margin = { top: 12, left: 3, right: 3, bottom: 8 };
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const tableWidth = pageW - margin.left - margin.right;

  autoTable(doc, {
    head: [HEADERS],
    body,
    startY: 15,
    margin,
    tableWidth,
    theme: 'grid',
    showHead: 'everyPage',
    styles: {
      fontSize: 6.5,
      cellPadding: { top: 0.75, right: 0.45, bottom: 0.75, left: 0.45 },
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'center',
      textColor: [15, 23, 42],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
      fillColor: [255, 255, 255],
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    headStyles: {
      fillColor: [241, 245, 249],
      textColor: [15, 23, 42],
      fontStyle: 'bold',
      fontSize: 6,
      halign: 'center',
      valign: 'middle',
      overflow: 'visible',
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    /* Widths sum to tableWidth (~204mm with 3mm side margins on A4 portrait). */
    columnStyles: {
      0: { cellWidth: 10 },
      1: { cellWidth: 8 },
      2: { cellWidth: 10 },
      3: { cellWidth: 10 },
      4: { cellWidth: 10 },
      5: { cellWidth: 12 },
      6: { cellWidth: 12 },
      7: { cellWidth: 26, halign: 'left' },
      8: { cellWidth: 53, halign: 'left' },
      9: { cellWidth: 8 },
      10: { cellWidth: 14, halign: 'center' },
      11: { cellWidth: 9 },
      12: { cellWidth: 11 },
      13: { cellWidth: 11 },
    },
    didParseCell: (data) => {
      if (data.section === 'head') {
        /* Match app header: one line per title; avoid mid-word breaks. */
        data.cell.styles.overflow = 'visible';
        return;
      }
      if (data.section !== 'body') return;
      const idx = data.column.index;
      /* Keep numeric / short fields on one line when possible. */
      if ([1, 2, 3, 4, 5, 6, 10, 11, 12, 13].includes(idx)) {
        data.cell.styles.overflow = 'visible';
      }
      if (idx === 7) {
        data.cell.styles.overflow = 'visible';
      }
    },
    didDrawPage: (data) => {
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      doc.text(title, margin.left, 10);
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      const footer = `Page ${data.pageNumber}`;
      doc.text(footer, pageW - margin.right - doc.getTextWidth(footer), pageH - 5);
    },
  });

  return doc;
}

/**
 * @param {{ rows: object[], shipmentCode?: string, trackingNumber?: string }} opts
 */
export function downloadPackingListPdf(opts) {
  const doc = buildPackingListPdfDoc(opts);
  const stem = safeFileStem(opts.shipmentCode, opts.trackingNumber);
  doc.save(`${stem}_packing-list.pdf`);
}

/**
 * Opens the print dialog using the same PDF as download (blob in iframe) so layout matches exactly.
 */
export function printPackingListA4(opts) {
  const doc = buildPackingListPdfDoc(opts);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Packing list print');
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '1px',
    height: '1px',
    border: '0',
    opacity: '0',
    pointerEvents: 'none',
  });
  document.body.appendChild(iframe);

  const cleanup = () => {
    URL.revokeObjectURL(url);
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  iframe.onload = () => {
    const win = iframe.contentWindow;
    if (!win) {
      cleanup();
      return;
    }
    try {
      win.focus();
      win.print();
    } catch (e) {
      console.error('Packing list print failed', e);
      cleanup();
      return;
    }
    win.addEventListener('afterprint', cleanup, { once: true });
    setTimeout(cleanup, 60_000);
  };

  iframe.onerror = () => {
    console.error('Packing list PDF load failed');
    cleanup();
  };

  iframe.src = url;
}
